import { faBan, faEllipsisVertical, faNoteSticky } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { Button, Tooltip } from '@shared/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useIsMobile } from '@shared/hooks/useMobile';
import { CheckCircle2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface BreadcrumbProps {
  entryId?: number;
  receiptId: number;
  receiptCode?: string | null;
  receiptStatus?: 'ABIERTA' | 'COMPLETADA' | 'CANCELADA';
  onNotesClick?: () => void;
  onFinalizeClick?: () => void;
  onCancelClick?: () => void;
  isFinalizeDisabled?: boolean;
  finalizeTooltip?: string;
  isCancelDisabled?: boolean;
  cancelTooltip?: string;
}

export const Breadcrumb = ({
  receiptId,
  receiptCode,
  receiptStatus,
  onNotesClick,
  onFinalizeClick,
  onCancelClick,
  isFinalizeDisabled = false,
  finalizeTooltip,
  isCancelDisabled = false,
  cancelTooltip,
}: BreadcrumbProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation('merchandiseV3');

  const breadcrumbs = useMemo(
    () => [
      ...(isMobile ? [] : [{ label: t('breadcrumb.merchandise', 'Mercancía'), path: '/merchandise' }]),
      {
        label: receiptCode || t('receive.breadcrumb.receipt', `Recepción #${receiptId}`, { receiptId }),
      },
    ],
    [isMobile, receiptCode, receiptId, t],
  );

  const headerActions = useMemo(() => {
    if (!onNotesClick && (receiptStatus !== 'ABIERTA' || (!onFinalizeClick && !onCancelClick))) {
      return [];
    }

    const actions = [];

    if (onNotesClick) {
      actions.push({
        id: 'receipt-notes',
        label: t('receive.notes.button', 'Notas'),
        // Botón de icono sin texto para mantener compacto el header de recepción.
        component: (
          <Tooltip
            trigger={
              <Button variant="outline" size="icon" onClick={onNotesClick} data-testid="header-action-receipt-notes">
                <FontAwesomeIcon icon={faNoteSticky} className="w-3.5 h-3.5" />
              </Button>
            }
            content={t('receive.notes.button', 'Notas')}
          />
        ),
        onClick: onNotesClick,
        variant: 'outline' as const,
      });
    }

    if (receiptStatus === 'ABIERTA' && isMobile && (onFinalizeClick || onCancelClick)) {
      actions.push({
        id: 'receipt-mobile-actions-menu',
        label: t('receive.actions.menu', 'Más acciones'),
        component: (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="header-action-receipt-mobile-menu">
                <FontAwesomeIcon icon={faEllipsisVertical} className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              {onCancelClick && (
                <DropdownMenuItem
                  onClick={onCancelClick}
                  disabled={isCancelDisabled}
                  className="cursor-pointer"
                  data-testid="receipt-mobile-cancel-action"
                >
                  <FontAwesomeIcon icon={faBan} className="mr-2 w-3.5 h-3.5" />
                  <span>{t('receive.cancel.button', 'Cancelar Recepción')}</span>
                </DropdownMenuItem>
              )}
              {onFinalizeClick && (
                <DropdownMenuItem
                  onClick={onFinalizeClick}
                  disabled={isFinalizeDisabled}
                  className="cursor-pointer bg-primary text-btn-text hover:bg-primary/90 focus:bg-primary/90 focus:text-btn-text"
                  data-testid="receipt-mobile-finalize-action"
                >
                  <CheckCircle2 className="mr-2 w-4 h-4" />
                  <span>{t('receive.finalize.button', 'Finalizar Recepción')}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        onClick: () => {},
        variant: 'outline' as const,
      });
    }

    if (receiptStatus === 'ABIERTA' && !isMobile && onCancelClick) {
      actions.push({
        id: 'cancel-receipt',
        label: t('receive.cancel.button', 'Cancelar Recepción'),
        icon: <FontAwesomeIcon icon={faBan} className="w-3.5 h-3.5" />,
        onClick: onCancelClick,
        variant: 'outline' as const,
        disabled: isCancelDisabled,
        tooltip: cancelTooltip,
      });
    }

    if (receiptStatus === 'ABIERTA' && !isMobile && onFinalizeClick) {
      actions.push({
        id: 'finalize-receipt',
        label: t('receive.finalize.button', 'Finalizar Recepción'),
        icon: <CheckCircle2 className="w-4 h-4" />,
        onClick: onFinalizeClick,
        variant: 'primary' as const,
        disabled: isFinalizeDisabled,
        tooltip: finalizeTooltip,
      });
    }

    return actions;
  }, [
    receiptStatus,
    onNotesClick,
    onFinalizeClick,
    onCancelClick,
    t,
    isFinalizeDisabled,
    finalizeTooltip,
    isMobile,
    isCancelDisabled,
    cancelTooltip,
  ]);

  usePageNavigation({ breadcrumbs, headerActions });

  return null;
};
